import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Save, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Bastion : ressaisie en masse des numéros des annonces Air-Bengue (format international). */
export const NumerosAirBengue = ({ adminId }) => {
  const [annonces, setAnnonces] = useState([]);
  const [sansNumero, setSansNumero] = useState(0);
  const [saisie, setSaisie] = useState({});
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(() => {
    axios
      .get(`${API}/admin/air-bengue/numeros`, { params: { admin_id: adminId } })
      .then((r) => {
        setAnnonces(r.data.annonces || []);
        setSansNumero(r.data.sans_numero || 0);
        setSaisie(
          Object.fromEntries((r.data.annonces || []).map((a) => [a.id, a.telephones[0] || ""])),
        );
      })
      .catch(() => toast.error("Impossible de charger les numéros Air-Bengue"));
  }, [adminId]);

  useEffect(charger, [charger]);

  const enregistrer = async () => {
    const numeros = Object.fromEntries(
      Object.entries(saisie)
        .filter(([, v]) => v.trim())
        .map(([id, v]) => [id, [v.trim()]]),
    );
    if (!Object.keys(numeros).length) {
      toast.error("Aucun numéro à enregistrer");
      return;
    }
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API}/admin/air-bengue/numeros`, {
        admin_id: adminId,
        numeros,
      });
      toast.success(`${data.mises_a_jour} annonce(s) mise(s) à jour`, {
        description: data.rejetes?.length
          ? `Refusés : ${data.rejetes.join(", ")} — ajoutez l'indicatif pays (+33, +32…)`
          : "Les numéros sont de nouveau visibles pour les abonnés.",
      });
      charger();
    } catch {
      toast.error("Enregistrement impossible");
    } finally {
      setEnvoi(false);
    }
  };

  if (!annonces.length) return null;

  return (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl p-5" data-testid="bastion-numeros-air">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold text-stone-900 flex items-center gap-2">
          <Phone size={18} className="text-[#0A5C36]" /> Numéros Air-Bengue ({annonces.length})
        </h3>
        <button
          onClick={enregistrer}
          disabled={envoi}
          data-testid="bastion-numeros-air-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#0A5C36] px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#074528] disabled:opacity-50"
        >
          <Save size={15} /> {envoi ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>

      {sansNumero > 0 && (
        <p
          data-testid="bastion-numeros-air-alerte"
          className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {sansNumero} annonce(s) sans numéro joignable : les abonnés ne voient aucun contact.
          Saisissez le numéro avec son indicatif pays (ex. +33 6 12 45 78 90).
        </p>
      )}

      <div className="mt-4 max-h-80 space-y-2 overflow-y-auto pr-1">
        {annonces.map((a) => (
          <div key={a.id} className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-stone-800">{a.nom}</div>
              <div className="truncate text-[11px] text-stone-500">
                {[a.ville, a.pays].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
            <input
              value={saisie[a.id] ?? ""}
              onChange={(e) => setSaisie({ ...saisie, [a.id]: e.target.value.replace(/[^\d+ ]/g, "") })}
              placeholder="+33 6 12 45 78 90"
              data-testid={`bastion-numero-air-${a.id}`}
              className={`w-52 rounded-xl border px-3 py-2 text-sm font-semibold tracking-wide outline-none transition-colors duration-200 ${
                a.telephones.length
                  ? "border-[#E5DFD5] text-stone-800"
                  : "border-amber-300 bg-amber-50/60 text-stone-800"
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default NumerosAirBengue;
